import { useMemo } from 'react';
import { clamp, scaleLinear } from '../../lib/scale';
import type { HeatmapDatum } from './model';

export interface HeatmapProps {
  /** row labels, top to bottom */
  rows: string[];
  /** column labels, left to right */
  cols: string[];
  /** grid values, indexed `data[rowIndex][colIndex]` */
  data: HeatmapDatum[][];
  /** the "normal" value - cells at/above render in the accent, below in muted grey */
  baseline?: number;
  /** cells whose `n` is below this render blank (thin sample) */
  minN?: number;
  /** print the value inside each cell (turn off for dense grids) */
  showValues?: boolean;
  /** render a column label every Nth column (keeps a dense axis readable) */
  colLabelStep?: number;
  /** cell width / height in svg units */
  cellW?: number;
  cellH?: number;
  /** formats the in-cell value + legend labels */
  format?: (n: number) => string;
  /** label for one column/row unit, used in tooltips (e.g. `ET`) */
  unit?: string;
  className?: string;
  ariaLabel?: string;
}

const PAD_L = 44;
const PAD_T = 10;
const LEGEND_W = 56;
const AXIS_H = 26;

interface Cell {
  x: number;
  y: number;
  fill: string;
  opacity: number;
  text: string;
  textFill: string;
  tip: string;
}

/** Weekday × time-of-day style grid. Accent = above the norm, grey = below, blank = thin sample. */
export const Heatmap = ({
  rows,
  cols,
  data,
  baseline = 1,
  minN = 0,
  showValues = true,
  colLabelStep = 1,
  cellW = 68,
  cellH = 28,
  format = (n) => n.toFixed(2),
  unit,
  className,
  ariaLabel = 'heatmap',
}: HeatmapProps) => {
  const { cells, vmax, vmin } = useMemo(() => {
    const live = data
      .flat()
      .filter((d) => d && d.value != null && (d.n ?? Infinity) >= minN)
      .map((d) => d.value as number);
    const maxAbove = Math.max(baseline, ...live);
    const minBelow = Math.min(baseline, ...live);
    const aboveOpacity = scaleLinear([baseline, maxAbove], [0.18, 1]);
    const belowOpacity = scaleLinear([baseline, minBelow], [0.18, 0.64]);
    const out: Cell[] = [];
    rows.forEach((rowLabel, r) => {
      cols.forEach((colLabel, c) => {
        const d = data[r]?.[c];
        if (!d || d.value == null || (d.n ?? Infinity) < minN) return;
        const v = d.value;
        const above = v >= baseline;
        const opacity = clamp(above ? aboveOpacity(v) : belowOpacity(v), 0.18, 1);
        out.push({
          x: PAD_L + c * cellW,
          y: PAD_T + r * cellH,
          fill: above ? 'var(--accent)' : 'var(--text-3)',
          opacity,
          text: format(v),
          textFill: above && opacity > 0.55 ? 'var(--bg)' : 'var(--text-2)',
          tip: `${rowLabel} ${colLabel}${unit ? ` ${unit}` : ''}: ${format(v)}×${d.n != null ? ` (n=${d.n})` : ''}`,
        });
      });
    });
    return { cells: out, vmax: maxAbove, vmin: minBelow };
  }, [rows, cols, data, baseline, minN, cellW, cellH, format, unit]);

  const gridW = cols.length * cellW;
  const gridH = rows.length * cellH;
  const width = PAD_L + gridW + LEGEND_W;
  const height = PAD_T + gridH + AXIS_H;
  const legendX = PAD_L + gridW + 16;
  const inset = Math.min(1, cellW * 0.05);
  // The cells use a diverging scale (grey below baseline, accent above), so the
  // legend must show both halves with the baseline as the faint midpoint.
  const tpos = clamp((baseline - vmin) / (vmax - vmin || 1), 0, 1);
  const tposPct = (tpos * 100).toFixed(1);
  const yBaseline = PAD_T + (1 - tpos) * gridH;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel} className={className}>
      {cells.map((cell) => (
        <g key={`${cell.x}-${cell.y}`}>
          <rect
            x={cell.x + inset}
            y={cell.y + inset}
            width={cellW - inset * 2}
            height={cellH - inset * 2}
            rx={2}
            fill={cell.fill}
            fillOpacity={cell.opacity}
          >
            <title>{cell.tip}</title>
          </rect>
          {showValues ? (
            <text
              x={cell.x + cellW / 2}
              y={cell.y + cellH / 2 + 3.5}
              textAnchor="middle"
              fontSize="10"
              fill={cell.textFill}
            >
              {cell.text}
            </text>
          ) : null}
        </g>
      ))}
      {rows.map((label, r) => (
        <text
          key={label}
          x={PAD_L - 8}
          y={PAD_T + r * cellH + cellH / 2 + 4}
          textAnchor="end"
          fontSize="11"
          fill="var(--text-3)"
        >
          {label}
        </text>
      ))}
      {cols.map((label, c) =>
        c % colLabelStep === 0 ? (
          <text
            key={label + c}
            x={PAD_L + c * cellW + cellW / 2}
            y={PAD_T + gridH + 17}
            textAnchor="middle"
            fontSize="9.5"
            fill="var(--text-3)"
          >
            {label}
          </text>
        ) : null,
      )}
      <defs>
        {/* 0% = bottom (vmin, grey) -> baseline (faint) -> 100% top (vmax, accent) */}
        <linearGradient id="heatmap-scale" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="var(--text-3)" stopOpacity="0.64" />
          <stop offset={`${tposPct}%`} stopColor="var(--text-3)" stopOpacity="0.18" />
          <stop offset={`${tposPct}%`} stopColor="var(--accent)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="1" />
        </linearGradient>
      </defs>
      <rect x={legendX} y={PAD_T} width="12" height={gridH} rx="2" fill="url(#heatmap-scale)" />
      <text x={legendX + 17} y={PAD_T + 9} fontSize="10" fill="var(--text-3)">
        {format(vmax)}×
      </text>
      <line x1={legendX} y1={yBaseline} x2={legendX + 12} y2={yBaseline} stroke="var(--bg)" strokeWidth="1.5" />
      <text x={legendX + 17} y={yBaseline + 3} fontSize="10" fill="var(--text-3)">
        {format(baseline)}×
      </text>
      <text x={legendX + 17} y={PAD_T + gridH} fontSize="10" fill="var(--text-3)">
        {format(vmin)}×
      </text>
    </svg>
  );
};
